import {action, observable, reaction, IReactionDisposer} from 'mobx';
import {BaseLayer} from './layers/BaseLayer';

export class LayeredImageController {
    @observable layers: BaseLayer[] = [];

    private canvasMap: Map<string, HTMLCanvasElement> = new Map();

    private disposerMap: Map<string, IReactionDisposer> = new Map();

    private containerElement: HTMLElement | null = null;

    private canvasContainerElement: HTMLDivElement | null = null;

    private canvasClassName: string = 'layered-image-canvas';

    private mountedCanvasIds: Set<string> = new Set();

    private canvasMountDisposer: IReactionDisposer | null = null;

    constructor(initialLayers: BaseLayer[] = []) {
        this.layers = initialLayers;
    }

    @action
    addLayer(layer: BaseLayer): void {
        this.layers.push(layer);
        this.layers.sort((a, b) => a.zIndex - b.zIndex);
    }

    @action
    removeLayer(layerId: string): void {
        this.detachLayer(layerId);
        this.layers = this.layers.filter((layer) => layer.id !== layerId);
    }

    getVisibleLayers(): BaseLayer[] {
        return this.layers.filter((layer) => layer.visible).sort((a, b) => a.zIndex - b.zIndex);
    }

    getLayer(layerId: string): BaseLayer | undefined {
        return this.layers.find((layer) => layer.id === layerId);
    }

    @action
    toggleLayerVisibility(layerId: string): void {
        const layer = this.getLayer(layerId);
        if (layer) {
            layer.visible = !layer.visible;
        }
    }

    @action
    clearLayers(): void {
        this.dispose();
        this.layers = [];
    }

    setContainer(container: HTMLElement | null): void {
        this.containerElement = container;
    }

    private createAndAttachCanvas(layer: BaseLayer, canvasClassName?: string): HTMLCanvasElement {
        const existingDisposer = this.disposerMap.get(layer.id);
        if (existingDisposer) {
            existingDisposer();
        }

        const canvas = document.createElement('canvas');
        canvas.className = canvasClassName || 'layered-image-canvas';
        canvas.setAttribute('data-layer-id', layer.id);

        this.canvasMap.set(layer.id, canvas);

        const dispose = reaction(
            () => ({
                visible: layer.visible,
                version: layer.layerVersion
            }),
            () => {
                if (!layer.visible) {
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }
                    return;
                }

                this.renderLayerOnCanvas(layer, canvas);
            }
        );

        this.disposerMap.set(layer.id, dispose);
        return canvas;
    }

    getAllCanvases(canvasClassName?: string): HTMLCanvasElement[] {
        const visibleLayers = this.getVisibleLayers();
        return visibleLayers.map((layer) => {
            let canvas = this.canvasMap.get(layer.id);
            if (!canvas) {
                canvas = this.createAndAttachCanvas(layer, canvasClassName);
            }
            return canvas;
        });
    }

    detachLayer(layerId: string): void {
        const disposer = this.disposerMap.get(layerId);
        if (disposer) {
            disposer();
            this.disposerMap.delete(layerId);
        }
        this.canvasMap.delete(layerId);
    }

    private renderLayerOnCanvas(layer: BaseLayer, canvas: HTMLCanvasElement): void {
        if (!this.containerElement) return;

        const rect = this.containerElement.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, rect.width, rect.height);
        layer.render(ctx, rect.width, rect.height);
    }

    dispose(): void {
        if (this.canvasMountDisposer) {
            this.canvasMountDisposer();
            this.canvasMountDisposer = null;
        }

        this.disposerMap.forEach((disposer) => disposer());
        this.disposerMap.clear();
        this.canvasMap.clear();
        this.mountedCanvasIds.clear();
    }

    mergeImageWithLayers(
        imageElement: HTMLImageElement,
        options?: {
            targetAspectRatio?: number;
            backgroundColor?: string;
            outputFormat?: string;
            layerMergeOptions?: Record<string, any>;
        }
    ): string | null {
        if (!imageElement || !imageElement.complete) {
            return null;
        }

        const {
            targetAspectRatio,
            backgroundColor = '#000000',
            outputFormat = 'image/png',
            layerMergeOptions
        } = options || {};

        const imgWidth = imageElement.naturalWidth;
        const imgHeight = imageElement.naturalHeight;

        const dimensions = this.calculateCanvasDimensions(imgWidth, imgHeight, targetAspectRatio);
        const canvas = this.setupCanvas(dimensions.canvasWidth, dimensions.canvasHeight, backgroundColor);
        if (!canvas) return null;

        this.drawImageOnCanvas(canvas, imageElement, dimensions);
        this.renderLayersOnCanvas(canvas, dimensions.canvasWidth, dimensions.canvasHeight, layerMergeOptions);

        return canvas.toDataURL(outputFormat);
    }

    private calculateCanvasDimensions(
        imgWidth: number,
        imgHeight: number,
        targetAspectRatio?: number
    ): {canvasWidth: number; canvasHeight: number; offsetX: number; offsetY: number} {
        if (!targetAspectRatio) {
            return {
                canvasWidth: imgWidth,
                canvasHeight: imgHeight,
                offsetX: 0,
                offsetY: 0
            };
        }

        const imageRatio = imgWidth / imgHeight;
        let canvasWidth: number;
        let canvasHeight: number;

        if (imageRatio > targetAspectRatio) {
            canvasWidth = imgWidth;
            canvasHeight = Math.round(imgWidth / targetAspectRatio);
        } else {
            canvasHeight = imgHeight;
            canvasWidth = Math.round(imgHeight * targetAspectRatio);
        }

        const offsetX = Math.round((canvasWidth - imgWidth) / 2);
        const offsetY = Math.round((canvasHeight - imgHeight) / 2);

        return {canvasWidth, canvasHeight, offsetX, offsetY};
    }

    private setupCanvas(width: number, height: number, backgroundColor: string): HTMLCanvasElement | null {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        canvas.width = width;
        canvas.height = height;

        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);

        return canvas;
    }

    private drawImageOnCanvas(
        canvas: HTMLCanvasElement,
        imageElement: HTMLImageElement,
        dimensions: {offsetX: number; offsetY: number}
    ): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const imgWidth = imageElement.naturalWidth;
        const imgHeight = imageElement.naturalHeight;

        ctx.drawImage(imageElement, dimensions.offsetX, dimensions.offsetY, imgWidth, imgHeight);
    }

    private renderLayersOnCanvas(
        canvas: HTMLCanvasElement,
        canvasWidth: number,
        canvasHeight: number,
        layerMergeOptions?: Record<string, any>
    ): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const layers = this.getVisibleLayers();
        layers.forEach((layer) => {
            layer.render(ctx, canvasWidth, canvasHeight, layerMergeOptions);
        });
    }
}
